import {
  Illustration,
  type IllustrationProps,
  mergeSxProps,
  RadioIndicator,
  Text,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type SxProps } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { SelectableBox } from "@src/appV2/redesign/components/SelectableBox";

import { type PayoutSchedule } from "../api/schema";
import { PayoutFeePills } from "./PayoutFeePills";
import { PayoutScheduleItemDisabledSection } from "./PayoutScheduleItemDisabledSection";

export interface PayoutScheduleItemProps {
  payoutSchedule: PayoutSchedule;
  title: string;
  description: string;
  illustrationType: IllustrationProps["type"];
  fee: number;
  selected: boolean;
  onSelect: (payoutSchedule: PayoutSchedule) => void;
  disabledMessage?: string;
  disabledCTA?: string;
  onDisabledCTAClick?: (payoutSchedule: PayoutSchedule) => void;
  sx?: SxProps<Theme>;
}

export function PayoutScheduleItem(props: PayoutScheduleItemProps) {
  const {
    payoutSchedule,
    title,
    description,
    illustrationType,
    fee,
    disabledMessage,
    disabledCTA,
    onDisabledCTAClick,
    selected,
    onSelect,
    sx,
  } = props;
  const disabled = isDefined(disabledMessage) && disabledMessage.length > 0;

  return (
    <SelectableBox
      data-testid={`payout-schedule-item-${payoutSchedule}`}
      variant="secondary"
      sx={mergeSxProps(
        (theme) => ({
          backgroundColor: disabled
            ? theme.palette.background.quaternary
            : theme.palette.background.tertiary,
          borderColor: theme.palette.border?.primary,
          overflow: "hidden",
          px: theme.spacing(5),
          "&.Mui-disabled": {
            borderColor: theme.palette.border?.primary,
          },
        }),
        sx
      )}
      isSelected={selected}
      // we don't pass in `disabled` because we want the PayoutScheduleItemDisabledSection to be clickable
      onToggleSelect={(didSelect) => {
        if (disabled) {
          onDisabledCTAClick?.(payoutSchedule);
        } else if (didSelect && !selected) {
          onSelect(payoutSchedule);
        }
      }}
    >
      <Stack sx={{ flexDirection: "row" }}>
        <Illustration
          type={illustrationType}
          sx={{ filter: disabled ? "grayscale(100%)" : undefined }}
        />
        <Stack sx={(theme) => ({ flex: 1, textAlign: "left", mx: theme.spacing(4) })}>
          <Text
            variant="body1"
            sx={(theme) => ({
              fontWeight: 500,
              mb: theme.spacing(1),
              color: disabled ? theme.palette.text.tertiary : theme.palette.text.primary,
            })}
          >
            {title}
          </Text>
          <Text
            variant="body2"
            sx={(theme) => ({
              color: disabled ? theme.palette.text.tertiary : theme.palette.text.primary,
            })}
          >
            {description}
          </Text>
          {disabled ? null : (
            <PayoutFeePills
              fee={fee}
              sx={(theme) => ({ alignSelf: "flex-start", mt: theme.spacing(3) })}
            />
          )}
        </Stack>
        <RadioIndicator selected={selected} />
      </Stack>
      {disabled ? (
        <PayoutScheduleItemDisabledSection
          disabledMessage={disabledMessage}
          disabledCTA={disabledCTA}
          sx={(theme) => ({ mt: theme.spacing(4) })}
          onDisabledCTAClick={() => {
            onDisabledCTAClick?.(payoutSchedule);
          }}
        />
      ) : null}
    </SelectableBox>
  );
}
